import { useContext, useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { AppContext } from "../../models/applicationState";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, TextField, ToggleButton, ToggleButtonGroup, Typography, Menu, MenuItem, InputAdornment, Tooltip } from "@mui/material";
import { ReactComponent as PublishTo } from '../../icons/publish_to.svg';
import { ReactComponent as PublishToFill } from '../../icons/publish_to_fill.svg';
import { ReactComponent as PublishCC } from '../../icons/publish_cc.svg';
import { ReactComponent as PublishCCFill } from '../../icons/publish_cc_fill.svg';
import { ReactComponent as PublishX } from '../../icons/publish_X.svg';
import { ReactComponent as PublishXFill } from '../../icons/publish_X_fill.svg';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams, GridRowId, GridRowModel, GridTreeNodeWithRender, GridValidRowModel } from "@mui/x-data-grid";
import { ApiUser, Meeting, MeetingStatus, UserRole } from "../../models";
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import config from "../../config";
import { MeetingHeader } from "../../components/meetingHeader";
import { Context } from "../../components/context";
import { bindActionCreators } from "../../actions/actionCreators";
import { ListActions } from "../../actions/listActions";
import * as listActions from "../../actions/listActions";
import { useTranslation } from "react-i18next";
import { bulkCreateUsers } from "../../services/userService";
import { useNavigate, useParams } from "react-router-dom";
import { ItemActions } from "../../actions/itemActions";
import * as itemActions from "../../actions/itemActions";
import { addPublishSystemCommentToItem } from "../../components/itemEditingPanel";
import { useMsal } from "@azure/msal-react";
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { TooltipIcon } from "../../components/tooltipIcon";
import dateFormatter from "../../util/dateFormatter";
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import BlockIcon from '@mui/icons-material/Block';
import MenuBookIcon from '@mui/icons-material/MenuBook';


export interface ParticipantDisplayInfo {
    id: string;
    email: string;
    notification: string;
    accessRight?: 'yes' | 'no';
}

const getAllMeetingEmails = (meeting: Meeting): string[] => {
    const allEmails: string[] = [];
    for (const email of [
        ...(meeting?.participants || []),
        ...(meeting?.viewers || []),
        ...(meeting?.items?.map(item => item.actor) || [])

    ]) {
        if (email && !allEmails.includes(email)) {
            allEmails.push(email);
        }
    }
    return allEmails;
}
const createTable = (meeting: Meeting): ParticipantDisplayInfo[] => {
    const allEmails = meeting?.status === MeetingStatus.Published ? meeting.viewers : getAllMeetingEmails(meeting);
    
    // Get internal domain from creator email
    const creatorEmail = meeting?.creator;
    const internalDomain = creatorEmail && creatorEmail.includes('@') ? creatorEmail.split('@')[1] : undefined;
    
    return allEmails ? allEmails.map(email => ({
        id: email,
        email: email,
        notification: "to",
        accessRight: 'yes'
    })) : [];
};

export const ReviewPage = () => {
    const appContext = useContext<AppContext>(Context);
    const [showEmailDistributionChoice, setShowEmailDistributionChoice] = useState(undefined);
    const [publishMenuAnchor, setPublishMenuAnchor] = useState<null | HTMLElement>(null);
    const { instance } = useMsal();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { meetingId } = useParams();
    const actions = useMemo(
        () => ({
            lists: bindActionCreators(
                listActions,
                appContext.dispatch
            ) as unknown as ListActions,
            items: bindActionCreators(
                itemActions,
                appContext.dispatch
            ) as unknown as ItemActions,
        }),
        [appContext.dispatch]
    );
    const [items, setItems] = useState<ParticipantDisplayInfo[]>(createTable(appContext.state.selectedList));
    const [newParticipant, setNewParticipant] = useState("");
    const [showBackConfirm, setShowBackConfirm] = useState(false);
    const initialItemsRef = useRef<ParticipantDisplayInfo[] | null>(null);

    // Derive internal domain from creator email; if not available, external detection is disabled
    const internalEmailDomain = useMemo(() => {
        const creatorEmail = appContext.state.selectedList?.creator;
        if (creatorEmail && creatorEmail.includes('@')) {
            const domain = creatorEmail.split('@')[1]?.toLowerCase();
            return domain ? `@${domain}` : undefined;
        }
        return undefined;
    }, [appContext.state.selectedList?.creator]);

    // Whether the currently selected meeting is published/closed
    const meetingPublished = appContext.state.selectedList?.status === MeetingStatus.Published;

    const processRowUpdate = (newRow: GridRowModel<ParticipantDisplayInfo>) => {
        // newRow.id is the existing row id (old email). When editing the email field
        // we want to update the item's id to the new email value.
        const oldId = newRow.id as string;
        const newEmail = (newRow as any).email as string;

        // Prevent duplicate emails when editing
        const duplicate = items.some(item => item.id !== oldId && item.email?.toLowerCase() === (newEmail || '').toLowerCase());
        if (duplicate) {
            setShowDuplicateEmailDialog(true);
            // return the previous row (no change)
            const prev = items.find(i => i.id === oldId);
            return prev as GridRowModel<ParticipantDisplayInfo> || newRow;
        }

        const updatedRow: GridRowModel<ParticipantDisplayInfo> = { ...newRow, id: newEmail } as GridRowModel<ParticipantDisplayInfo>;

        // Update local state 
        const newItems = items.map(item => item.id === oldId ? (updatedRow as ParticipantDisplayInfo) : item);
        setItems(newItems);

        return updatedRow;
    };
    
    // which publish mode is selected in the dropdown (does not auto-publish)
    const [publishOption, setPublishOption] = useState<'selected' | 'everyone' | 'internal' | 'external'>('selected');


    useEffect(() => {
        if (meetingId && appContext.state.selectedList?.id !== meetingId) {
            actions.lists.load(meetingId);
        }
        const itemsOnList = createTable(appContext.state.selectedList);
        setItems(itemsOnList);
        initialItemsRef.current = itemsOnList;
    }, [actions.lists, appContext.state.selectedList, appContext.state.selectedList?.items, meetingId, navigate]);

    // Load items for selected list
    useEffect(() => {
        if (appContext.state.selectedList?.id && !appContext.state.selectedList.items) {
            const loadListItems = async (listId: string) => {
                await actions.items.list(listId);
            }
            loadListItems(appContext.state.selectedList.id)
        }
    }, [actions.items, appContext.state.selectedList?.id, appContext.state.selectedList?.items, navigate])

    const translate = (col: GridColDef): GridColDef => {
        return { ...col, headerName: t(col.headerName) };
    };

    const itemsEqual = (a: ParticipantDisplayInfo[] | undefined, b: ParticipantDisplayInfo[] | undefined) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        const sortFn = (x: ParticipantDisplayInfo, y: ParticipantDisplayInfo) => (x.email || '').localeCompare(y.email || '');
        const aa = [...a].sort(sortFn);
        const bb = [...b].sort(sortFn);
        for (let i = 0; i < aa.length; i++) {
            if ((aa[i].email || '').toLowerCase() !== (bb[i].email || '').toLowerCase()) return false;
            if ((aa[i].notification || '') !== (bb[i].notification || '')) return false;
        }
        return true;
    };

    const hasChanges = useMemo(() => {
        return !itemsEqual(items, initialItemsRef.current || []);
    }, [items, appContext.state.selectedList?.id]);

    const publishMeetingWithUsers = async (usersInput: ParticipantDisplayInfo[]): Promise<Meeting> => {
        const meeting = appContext.state.selectedList;
        const oldStatus = meeting.status;
        const ownerEmail = meeting?.creator?.toLowerCase();
        const viewers = usersInput
            .filter(item =>
                (item.notification === "to" || item.notification === "cc" || item.notification === "x") &&
                item.accessRight === 'yes'
            )
            .map(item => item.email);
        // Ensure creator is always included as viewer
        if (ownerEmail && meeting?.creator && !viewers.some(v => v?.toLowerCase() === ownerEmail)) {
            viewers.push(meeting.creator);
        }
        meeting.viewers = viewers;
        meeting.status = MeetingStatus.Published;
        const savedMeeting = await actions.lists.save(meeting);

        if (appContext.state.selectedList?.items && oldStatus !== MeetingStatus.Published) {
            const userFullName = (appContext.state.invoker.lastName && appContext.state.invoker.firstName) ? (appContext.state.invoker?.firstName + ' ' + appContext.state.invoker?.lastName) : instance.getActiveAccount()?.name;
            await Promise.all(appContext.state.selectedList.items.map(item => {
                addPublishSystemCommentToItem(item, userFullName, t);
                return actions.items.save(appContext.state.selectedList.id, item);
            }
            ));
        }

        const users: ApiUser[] = usersInput.filter(
            u => u.email && u.email.indexOf('@') > 0
        ).map(u => ({ email: u.email, role: UserRole.Actor }));
        await bulkCreateUsers(users);
        return savedMeeting;
    }

    const publishAsync = async (sendEmail: boolean) => {
        const savedMeeting = await publishMeetingWithUsers(items);
        if (!sendEmail) {
            return;
        }
        const toUsers = items.filter(item => item.notification == "to" && item.email !== appContext.state.selectedList?.creator);
        const ccUsers = items.filter(item => item.notification == "cc" && item.email !== appContext.state.selectedList?.creator);
        if (toUsers.length > 0 || ccUsers.length > 0) {
            const toList = toUsers.map(item => item.email).join(';');
            const ccList = ccUsers.map(item => item.email).join(';');

            const subject = t('mail_template.meeting_minutes_subject',
                {
                    meetingName: appContext.state.selectedList?.name,
                    published_on: dateFormatter.format(new Date(savedMeeting.publishedOn))
                });
            const bodyContent = t('mail_template.meeting_minutes_content', { baseUrl: config.web.baseUrl, meetingId: appContext.state.selectedList?.id, creator: appContext.state.selectedList.creator });
            const ccListContent = ccList ? `&cc=${ccList}` : '';
            const mailtoUrl = `mailto:${toList}?subject=${encodeURIComponent(subject)}${ccListContent}&body=${encodeURIComponent(bodyContent)}`;

            // Open the mail client
            window.location.href = mailtoUrl;
        }
    }

    const handleDelete = (id: GridRowId) => () => {
        const item = items.find(r => r.id == id.toString());
        if (!item) return;

        // Prevent deleting the meeting owner/creator
        const ownerEmail = appContext.state.selectedList?.creator;
        if (ownerEmail && item.email && item.email.toLowerCase() === ownerEmail.toLowerCase()) {
            console.warn('Attempt to delete meeting owner prevented');
            return;
        }

        const newItems = items.filter((row) => row.id !== item.id);
        setItems(newItems);
    };

    const getActions = (id: GridRowId, row: GridValidRowModel) => {
        const ownerEmail = appContext.state.selectedList?.creator;
        const isOwner = !!(row?.email && ownerEmail && (row.email as string).toLowerCase() === ownerEmail.toLowerCase());

        return [(
            <GridActionsCellItem
                key="delete"
                icon={<TooltipIcon titleKey={isOwner ? 'review.cannot_delete_owner' : 'general.delete'} icon={<DeleteIcon />} />}
                label="Delete"
                onClick={isOwner ? undefined : handleDelete(id)}
                disabled={isOwner}
                style={{
                    color: isOwner
                        ? "var(--mui-palette-grey-400)"
                        : "var(--mui-palette-primary-main)"
                }}
            />
        )];
    }

    const columns: GridColDef[] = [
        { 
            field: 'email', 
            headerName: appContext.state.selectedList?.status === MeetingStatus.Published ? 'library.viewers' : 'review.email', 
            width: 320,
            editable: true,
            renderHeader: (params) => <span style={{ fontWeight: 700 }}>{params.colDef.headerName}</span>
        },
        {
            field: 'external',
            headerName: '',
            width: 120,
            editable: false,
            renderHeader: (params) => <span style={{ fontWeight: 700 }}>{params.colDef.headerName}</span>,
            renderCell: (params) => {
                const email = params.row.email;
                const isExternal = email && internalEmailDomain && email.includes('@') && !email.toLowerCase().endsWith(internalEmailDomain);
                return isExternal ? <span style={{ color: '#000000ff', fontWeight: 500 }}>{t('review.external')}</span> : null;
            }
        },
        // Flexible spacer so publishing options sit at the far right
        {
            field: '__spacer',
            headerName: '',
            flex: 1,
            minWidth: 0,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderHeader: () => null,
            renderCell: () => null,
        },
        // conditionally include notification + small spacer when NOT published
        ...(appContext.state.selectedList?.status !== MeetingStatus.Published ? [
            ({
                field: 'notification',
                headerName: 'review.notification',
                headerAlign: 'right',
                align: 'center',
                width: 150,
                editable: false,
                renderHeader: (params) => <span style={{ fontWeight: 700 }}>{params.colDef.headerName}</span>,
                renderCell: (params) => {
                    // Compute a preview notification per row based on publishOption (visual only)
                    const row = params.row as ParticipantDisplayInfo;
                    const email = (row.email || '').toString().toLowerCase();
                    let preview: string = row.notification;
                    switch (publishOption) {
                        case 'everyone':
                            preview = 'to';
                            break;
                        case 'internal':
                            preview = (internalEmailDomain && email && email.endsWith(internalEmailDomain)) ? 'to' : 'x';
                            break;
                        case 'external':
                            preview = (email && email.includes('@') && (!internalEmailDomain || !email.endsWith(internalEmailDomain))) ? 'to' : 'x';
                            break;
                        default:
                            preview = row.notification;
                    }

                    // Determine whether this row represents an email address
                    const isEmailEntry = !!(email && email.indexOf('@') > 0);
                    const disabledForNonEmail = !isEmailEntry;

                    const previewForRender = disabledForNonEmail ? null : preview;

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Tooltip title={disabledForNonEmail ? t('review.publish_not_email') : ''}>
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <ToggleButtonGroup
                                        value={previewForRender}
                                        exclusive
                                        onChange={(
                                            event: React.MouseEvent<HTMLElement>,
                                            selection: string | null,
                                        ) => handleToggle(event, selection, params)}
                                        aria-label={t('review.notification_label')}
                                        disabled={disabledForNonEmail}
                                    >
                                        <Tooltip title={t('publish.tooltip.to') as string}>
                                            <ToggleButton
                                                size="small"
                                                value="to"
                                                aria-label="To"
                                                color='primary'
                                                selected={previewForRender === "to"}
                                                disabled={disabledForNonEmail}
                                                sx={[disabledForNonEmail ? {
                                                    opacity: 0.5
                                                } : {
                                                    opacity: 1
                                                }]}
                                            >
                                                {previewForRender === "to" ? <PublishToFill /> : <PublishTo />}
                                            </ToggleButton>
                                        </Tooltip>
                                        <Tooltip title={t('publish.tooltip.cc') as string}>
                                            <ToggleButton
                                                size="small"
                                                value="cc"
                                                aria-label="CC"
                                                color='primary'
                                                selected={previewForRender === "cc"}
                                                disabled={disabledForNonEmail}
                                                sx={[disabledForNonEmail ? {
                                                    opacity: 0.5
                                                } : {
                                                    opacity: 1
                                                }]}
                                            >
                                                {previewForRender === "cc" ? <PublishCCFill /> : <PublishCC />}
                                            </ToggleButton>
                                        </Tooltip>
                                        <Tooltip title={t('publish.tooltip.x') as string}>
                                            <ToggleButton
                                                size="small"
                                                value="x"
                                                aria-label="X"
                                                color='error'
                                                selected={previewForRender === "x"}
                                                disabled={disabledForNonEmail}
                                                sx={[disabledForNonEmail ? {
                                                    opacity: 0.5
                                                } : {
                                                    opacity: 1
                                                }]}
                                            >
                                                {previewForRender === "x" ? <PublishXFill /> : <PublishX />}
                                            </ToggleButton>
                                        </Tooltip>
                                    </ToggleButtonGroup>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                }
            } as GridColDef),
            ({
                field: '__access_rights',
                headerName: 'review.access_rights',
                width: 120,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                editable: false,
                renderHeader: (params) => <span style={{ fontWeight: 700 }}>{params.colDef.headerName}</span>,
                renderCell: (params) => {
                    const row = params.row as ParticipantDisplayInfo;
                    const email = (row.email || '').toString().toLowerCase();
                    const isEmailEntry = !!(email && email.indexOf('@') > 0);
                    const disabledForNonEmail = !isEmailEntry;
                    const value = isEmailEntry ? (row.accessRight || 'yes') : null;
                    const publishingToOrCc = isEmailEntry && (row.notification === 'to' || row.notification === 'cc');
                    const ownerEmail = appContext.state.selectedList?.creator?.toLowerCase();
                    const isOwnerRow = isEmailEntry && !!ownerEmail && email === ownerEmail;

                    const handleAccessToggle = (
                        event: React.MouseEvent<HTMLElement>,
                        selection: 'yes' | 'no' | null
                    ) => {
                        event.preventDefault();
                        if (!selection) return;
                        if (publishingToOrCc && selection === 'no') {
                            return;
                        }

                        const updatedRow = { ...row, accessRight: selection } as GridRowModel<ParticipantDisplayInfo>;
                        params.api.updateRows([{ id: row.id, data: updatedRow }]);
                        setItems(prev => prev.map(i => i.id === updatedRow.id ? (updatedRow as ParticipantDisplayInfo) : i));
                    };

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <ToggleButtonGroup
                                value={disabledForNonEmail ? null : value}
                                exclusive
                                onChange={(event, selection) => handleAccessToggle(event, selection as 'yes' | 'no' | null)}
                                aria-label={t('review.access_rights_label')}
                            >
                                <Tooltip title={disabledForNonEmail ? t('review.access_rights_not_email') : t('review.access_rights_tooltip.yes') as string}>
                                    <span>
                                        <ToggleButton
                                            size="small"
                                            value="yes"
                                            aria-label="Yes"
                                            color="primary"
                                            selected={isEmailEntry && value === 'yes'}
                                            disabled={disabledForNonEmail || isOwnerRow}
                                            sx={{ opacity: (disabledForNonEmail || isOwnerRow) ? 0.9 : 1 }}
                                        >
                                            <MenuBookIcon sx={{ fontSize: '22px' }} />
                                        </ToggleButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title={disabledForNonEmail ? t('review.access_rights_not_email') : t('review.access_rights_tooltip.no') as string}>
                                    <span>
                                        <ToggleButton
                                            size="small"
                                            value="no"
                                            aria-label="No"
                                            color="error"
                                            selected={isEmailEntry && value === 'no'}
                                            disabled={disabledForNonEmail || publishingToOrCc || isOwnerRow}
                                            sx={{ opacity: (disabledForNonEmail || publishingToOrCc || isOwnerRow) ? 0.9 : 1 }}
                                        >
                                            <BlockIcon sx={{ fontSize: '22px' }} />
                                        </ToggleButton>
                                    </span>
                                </Tooltip>
                            </ToggleButtonGroup>
                        </Box>
                    );
                },
            } as GridColDef),
            // small spacer to the right of the publishing options
            ({
                field: '__small_spacer',
                headerName: '',
                width: 20,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderHeader: () => null,
                renderCell: (params) => {
                    // Provide a delete button in the small spacer to remove the row (email/name)
                    const rowEmail = params.row?.email as string | undefined;
                    const ownerEmail = appContext.state.selectedList?.creator;
                    const isOwner = !!(rowEmail && ownerEmail && rowEmail.toLowerCase() === ownerEmail.toLowerCase());
                    const tooltipText = isOwner ? t('review.cannot_delete_owner') : t('general.delete');

                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Tooltip title={tooltipText}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={isOwner ? undefined : handleDelete(params.row.id)}
                                        aria-label={tooltipText}
                                        disabled={isOwner}
                                        sx={[isOwner ? {
                                            color: 'grey.400'
                                        } : {
                                            color: 'primary.main'
                                        }]}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    );
                },
            } as GridColDef)
        ] : []),
        // include actions column when published
        ...(appContext.state.selectedList?.status === MeetingStatus.Published ? [
            ({
                field: 'actions',
                type: 'actions',
                width: 170,
                headerName: 'general.delete',
                cellClassName: 'actions',
                renderHeader: (params) => <span style={{ fontWeight: 700 }}>{params.colDef.headerName}</span>,
                getActions: ({ id, row }) => getActions(id, row)
            } as GridColDef)
        ] : [])
    ];

    const translatedColumns: GridColDef[] = columns.map(translate);

    const handleClose = (event, reason) => {
        if (reason && reason === "backdropClick")
            return;
        setShowEmailDistributionChoice(undefined);
    }

    const handleToggle = (
        event: React.MouseEvent<HTMLElement>,
        selected: string | null,
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>
    ) => {
        event.preventDefault();

        // Guard against null selection
        if (!selected) {
            return;
        }

        setPublishOption('selected');

        const ownerEmail = appContext.state.selectedList?.creator?.toLowerCase();
        const rowEmail = (params.row?.email || '').toString().toLowerCase();
        const isOwner = !!(ownerEmail && rowEmail && ownerEmail === rowEmail);

        const updatedRow = {
            ...params.row,
            notification: selected,
            accessRight: isOwner ? 'yes' : (selected === 'x' ? 'no' : 'yes')
        } as GridRowModel<ParticipantDisplayInfo>;

        // Update the DataGrid with the new row object
        params.api.updateRows([{ id: params.row.id, data: updatedRow }]);

        // Update our local items state immutably
        setItems(prev => prev.map(i => i.id === updatedRow.id ? (updatedRow as ParticipantDisplayInfo) : i));

        // Perform any persistence / side-effects
        processRowUpdate(updatedRow);
    };

    const handleNewParticipantInput = (value: string) => {
        setNewParticipant(value);
    }

    const handleAddParticipant = (ev: React.KeyboardEvent) => {
        if (ev.key === "Enter") {
            ev.preventDefault();
            handleAddParticipantButtonClick();
        }
    }
    const [showDuplicateEmailDialog, setShowDuplicateEmailDialog] = useState(false);

    const handleAddParticipantButtonClick = () => {
        if (newParticipant.trim() !== "") {
            const email = newParticipant.trim();
            // Check for duplicate email
            const alreadyExists = items.some(item => item.email.toLowerCase() === email.toLowerCase());
            if (alreadyExists) {
                setShowDuplicateEmailDialog(true);
                return;
            }
            // Check if the new email is external (for display purposes only)
            const creatorEmail = appContext.state.selectedList?.creator;
            const internalDomain = creatorEmail && creatorEmail.includes('@') ? creatorEmail.split('@')[1] : undefined;
            const isExternal = email && email.includes('@') && internalDomain && 
                !email.toLowerCase().endsWith(`@${internalDomain.toLowerCase()}`);
            const newItems = [...items, { 
                id: email, 
                email: email, 
                notification: "to"  // All emails default to distribute
            }];
            setItems(newItems);
            setNewParticipant("");
            
        }
    }
    const handlePublishMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setPublishMenuAnchor(event.currentTarget);
    };

    const handlePublishMenuClose = () => {
        setPublishMenuAnchor(null);
    };

    // When a menu item is selected, only set the publishOption; do not publish immediately
    const handlePublishOption = (option: 'selected' | 'everyone' | 'internal' | 'external') => {
        setPublishOption(option);
        if (option && option !== 'selected') {
            const ownerEmail = appContext.state.selectedList?.creator?.toLowerCase();
            const updatedItems = items.map(item => {
                const email = (item.email || '').toLowerCase();
                let notification: 'to' | 'cc' | 'x' = item.notification as any;
                switch (option) {
                    case 'everyone':
                        notification = 'to';
                        break;
                    case 'internal':
                        notification = (internalEmailDomain && email && email.endsWith(internalEmailDomain)) ? 'to' : 'x';
                        break;
                    case 'external':
                        notification = (email && email.includes('@') && (internalEmailDomain ? !email.endsWith(internalEmailDomain) : true)) ? 'to' : 'x';
                        break;
                    default:
                        notification = item.notification as any;
                }

                const isOwner = !!(ownerEmail && email && ownerEmail === email);
                const accessRight: 'yes' | 'no' = isOwner ? 'yes' : (notification === 'x' ? 'no' : 'yes');
                return { ...item, notification, accessRight };
            });

            setItems(updatedItems);
        }

        handlePublishMenuClose();
    };

    const executeSelectedPublish = async () => {
        // If the meeting is already published, treat this button as "Update access" 
        if (appContext.state.selectedList?.status === MeetingStatus.Published) {
            const saved = await publishMeetingWithUsers(items);
            initialItemsRef.current = createTable(saved);
            return;
        }
        switch (publishOption) {
            case 'selected':
                await handlePublishSelected();
                break;
            case 'everyone':
                await publishAsync(true);
                break;
            case 'internal': {
                const internalParticipants = items.filter(item => {
                    const email = item.email?.toLowerCase();
                    return internalEmailDomain && email && email.endsWith(internalEmailDomain);
                });
                if (internalParticipants.length === 0) {
                    console.warn(t('No internal participants found for publishing'));
                    return;
                }
                await publishMeetingWithUsers(internalParticipants);
                break;
            }
            case 'external': {
                const externalParticipants = items.filter(item => {
                    const email = item.email?.toLowerCase();
                    return email && email.includes('@') && (internalEmailDomain ? !email.endsWith(internalEmailDomain) : true);
                });
                if (externalParticipants.length === 0) {
                    console.warn(t('No external participants found for publishing'));
                    return;
                }
                await publishMeetingWithUsers(externalParticipants);
                break;
            }
            default:
                console.error(t('Unknown publish option'));
                break;
        }
    };

    const handlePublishSelected = async () => {
        console.log(t('Publishing to selected participants'));

        const selectedParticipants = items.filter(item => {
            const hasAccess = (item.accessRight || 'yes') !== 'no';
            return hasAccess && (item.notification === 'to' || item.notification === 'cc' || item.notification === 'x');
        });
        if (selectedParticipants.length === 0) {
            console.warn(t('No participants selected for publishing'));
            return;
        }

        console.log(t('Selected participants for distribution:'), selectedParticipants.map(p => p.email));

        await publishMeetingWithUsers(selectedParticipants);
        console.log(t('Successfully published to selected participants'));
    };

    return (
        <Box>
            {/* <CssBaseline /> */}
            <MeetingHeader meetingName={appContext.state.selectedList?.name}
                onBack={() => {
                    if (meetingPublished && hasChanges) {
                        setShowBackConfirm(true);
                    } else {
                        navigate(-1);
                    }
                }}
                actionButtons={[
                    (
                        <Box key={'publish-split'} sx={{ display: 'inline-flex', alignItems: 'center', borderRadius: '4px', overflow: 'hidden', boxShadow: 'none' }}>
                            {/* show contextual tooltip: different when updating access on a published meeting */}
                            <Tooltip title={meetingPublished ? t('review.update_access_tooltip') : t('review.publish_tooltip')}>
                                <Button
                                    key={'publish-main'}
                                    variant="contained"
                                    disableElevation
                                    disableRipple
                                    disableFocusRipple
                                    sx={{
                                        backgroundColor: '#1E2A78',
                                        color: '#FFFFFF',
                                        borderRadius: 0,
                                        minWidth: 96,
                                        width: '177px',
                                        height: 40,
                                        textTransform: 'none',
                                        border: 'none',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: '#1E2A78'
                                        },
                                        '&:active': {
                                            backgroundColor: '#1a235f'
                                        },
                                        '&:focus': {
                                            outline: 'none'
                                        }
                                    }}
                                    onClick={async () => {
                                        await executeSelectedPublish();
                                        navigate(`/library/${appContext.state.selectedList?.id}/published`, { state: { blockPrevious: true } });
                                    }}
                                >
                                    {appContext.state.selectedList?.status === MeetingStatus.Published
                                        ? t('review.update_access')
                                        : (publishOption === 'selected'
                                            ? t('review.publish_button')
                                            : (publishOption === 'everyone'
                                                ? t('review.publish_everyone')
                                                : (publishOption === 'internal' ? t('review.publish_internal') : t('review.publish_external'))))}
                                </Button>
                            </Tooltip>

                            {/* no visible divider: transparent left border so clickable areas remain separate but seamless */}
                            <Button
                                key={'publish-dropdown'}
                                variant="contained"
                                disableElevation
                                disableRipple
                                disableFocusRipple
                                sx={{
                                    backgroundColor: '#1E2A78',
                                    color: '#FFFFFF',
                                    borderRadius: 0,
                                    minWidth: 40,
                                    px: 0,
                                    height: 40,
                                    border: 'none',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1E2A78'
                                    },
                                    '&:active': {
                                        backgroundColor: '#1a235f'
                                    },
                                    '&:focus': {
                                        outline: 'none'
                                    }
                                }}
                                onClick={(event) => meetingPublished ? undefined : handlePublishMenuOpen(event)}
                                disabled={meetingPublished}
                                aria-haspopup={meetingPublished ? undefined : 'true'}
                                aria-controls={meetingPublished ? undefined : (publishMenuAnchor ? 'publish-menu' : undefined)}
                                aria-expanded={meetingPublished ? undefined : (publishMenuAnchor ? 'true' : undefined)}
                            >
                            <ArrowDropDownIcon />
                            </Button>
                        </Box>
                    )
                ]}
            />
            <Dialog
                fullWidth={true}
                maxWidth="xs"
                onClose={() => setShowBackConfirm(false)}
                aria-labelledby="back-confirmation-dialog"
                open={showBackConfirm} slotProps={{ paper: {
                    style: {
                        borderRadius: '8px',
                        backgroundColor: 'white',
                    }
                } }}
            >
                <Box>
                    <Box
                        sx={{
                            width: '8px',
                            backgroundColor: 'white',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            borderTopLeftRadius: 'inherit',
                            borderBottomLeftRadius: 'inherit'
                        }}
                    />
                </Box>

                <DialogTitle id="back-confirmation-dialog" sx={{ m: 0, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" component="span">
                            {t('review.back_confirmation_title')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => setShowBackConfirm(false)}
                    sx={theme => ({
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme.palette.grey[500]
                    })}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ pt: 0 }}>
                    <DialogContentText
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 400,
                            whiteSpace: 'pre-line'
                        }}
                    >
                        {t('review.back_confirmation_content') }
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="outlined" onClick={() => {
                        setShowBackConfirm(false);
                        const original = initialItemsRef.current || createTable(appContext.state.selectedList);
                        setItems(original);
                        navigate(-1);
                    }}>
                        {t('review.discard_changes')}
                    </Button>
                    <Button variant="contained" onClick={async () => {
                        const saved = await publishMeetingWithUsers(items);
                        initialItemsRef.current = createTable(saved);
                        setShowBackConfirm(false);
                        navigate(-1);
                    }} autoFocus>
                        {t('review.set_rights')}
                    </Button>
                </DialogActions>
            </Dialog>
            <Menu
                id="publish-menu"
                anchorEl={publishMenuAnchor}
                open={Boolean(publishMenuAnchor)}
                onClose={handlePublishMenuClose}
            >
                <MenuItem onClick={() => handlePublishOption('everyone')}>{t('review.publish_everyone')}</MenuItem>
                <MenuItem onClick={() => handlePublishOption('internal')}>{t('review.publish_internal')}</MenuItem>
                <MenuItem onClick={() => handlePublishOption('external')}>{t('review.publish_external')}</MenuItem>
            </Menu>
            <div className='addParticipantWrapper'>
                <Box sx={{ width: '100%', boxSizing: 'border-box', border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'hidden', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <TextField
                            value={newParticipant}
                            sx={{
                                width: '300px',
                                marginRight: '10px',
                                height: '32px',
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: '#e0e0e0',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#bdbdbd',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#bdbdbd',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: 'none',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    height: '20px',
                                    padding: '6px 8px',
                                    '&::placeholder': {
                                        color: '#5a5a5aff',
                                        opacity: 1,
                                    }
                                },
                            }}
                            onChange={(ev) => handleNewParticipantInput(ev.target.value || "")}
                            onKeyDown={(ev) => handleAddParticipant(ev)}
                            placeholder={t('review.participant_placeholder')} slotProps={{ input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton color='primary' onClick={handleAddParticipantButtonClick} edge="end">
                                            <PersonAddAltIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            } }}
                        />
                    </Box>
                </Box>
            </div>
            <div style={{ height: '70vh', width: '100%', backgroundColor: '#FFFFFF' }}>
                <DataGrid rows={items}
                    columns={translatedColumns}
                    disableRowSelectionOnClick={true}
                    rowSelection={false}
                    processRowUpdate={processRowUpdate}
                    hideFooter={true}
                />
            </div>
            {showEmailDistributionChoice && (
                <Dialog
                    fullWidth={true}
                    maxWidth="sm"
                    onClose={handleClose}
                    aria-labelledby="confirmation-dialog"
                    open={showEmailDistributionChoice} slotProps={{ paper: {
                        style: {
                            backgroundColor: 'white'
                        }
                    } }}
                >
                    <DialogTitle sx={{ m: 0, p: 2 }} id="confirmation-dialog-title" />
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowEmailDistributionChoice(undefined)}
                        sx={theme => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500]
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent>
                        <Typography variant="body2">
                            {t('library.distribute_email')}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" onClick={() => {
                            navigate(`/library/${showEmailDistributionChoice}/published`, { state: { blockPrevious: true } });
                            publishAsync(false);
                            setShowEmailDistributionChoice(undefined);
                        }}>
                            <Typography variant="button" sx={{
                                display: "block"
                            }}>
                                {t('button.no')}
                            </Typography>
                        </Button>
                        <Button variant="contained" onClick={() => {
                            navigate(`/library/${showEmailDistributionChoice}/published`, { state: { blockPrevious: true } });
                            publishAsync(true);
                            setShowEmailDistributionChoice(undefined);
                        }}>
                            <Typography variant="button" sx={{
                                display: "block"
                            }}>
                                {t('button.yes')}
                            </Typography>
                        </Button>
                    </DialogActions>
                </Dialog >
            )}
            {/* Duplicate Email Alert Dialog */}
            <Dialog
                open={showDuplicateEmailDialog}
                onClose={() => {
                    setShowDuplicateEmailDialog(false);
                    setNewParticipant("");
                }}
                maxWidth="sm"
                fullWidth slotProps={{ paper: {
                    style: { width: 400, maxWidth: '90%' }
                } }}
            >
                <DialogTitle sx={{ fontSize: 20, fontWeight: 600, mb: 0, pb: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 550, fontSize: 20, mb: 0, pb: 2 }}>
                        {t('review.duplicate_email_headline')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        {t('review.duplicate_email_message')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => {
                        setShowDuplicateEmailDialog(false);
                        setNewParticipant("");
                    }}>
                        <Typography variant="button" sx={{
                            display: "block"
                        }}>
                            {t('button.ok', 'OK')}
                        </Typography>
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
